import React, {useState, useRef} from "react";
import FloatingTextInput from "@/components/floating-text-input";
import Button from "@/components/button";
import css from "./index.module.scss";
import RevealedText from "@/components/revealedText";
import Checkbox from "@/components/checkbox";
import Modal from "@/components/modal";
import HCaptcha from "@hcaptcha/react-hcaptcha";
import { useEffect } from "react";
import { useRouter } from "next/router";
import sendDatalayerEvent from "scripts/datalayer";



export default function ContactSection({
  color = "yellow",
  compact = false,
}) {
  const [form, setForm] = useState({
    name: useRef(),
    email: useRef(),
    topic: useRef(),
    message: useRef(),
    tos: useRef(),
  });
  const [cta, setCta] = useState("Contact Us");
  const [allowSubmit, setAllowSubmit] = useState(true);
  const modalRef = useRef();
  const [submitted, setSubmitted] = useState(false);
  const [modalHeader, setModalHeader] = useState("");
  const [modalText, setModalText] = useState("");

  const [captchaToken, setCaptchaToken] = useState(null);
  const captchaRef = useRef(null);
  const router = useRouter();


  async function submit(){
    if (allowSubmit || true){
      setAllowSubmit(false);
      let allValid = true;
      for (const [key, element] of Object.entries(form)){
        if (!await element.current.isValid()){
          allValid = false;
        }
      }

      if (allValid){
        setCta("Submitting");
        fetch("/api/contact", {
          method: "POST",
          mode: "cors",
          cache: "no-cache",
          credentials: "same-origin",
          headers: {
            "Content-Type": "application/json",
          },
          redirect: "follow",
          referrerPolicy: "no-referrer",
          body: JSON.stringify({
            email: form.email.current.getValue(),
            name: form.name.current.getValue(),
            topic: form.topic.current.getValue(),
            message: form.message.current.getValue(),
            referer: window.location.href,
            captchaToken,
          }),
        })
          .then((res) => {
            if (res.ok){
              setSubmitted(true);
              window.lastDirtied = null;
              setCta("Sent!");
              modalRef.current.show();
              setModalHeader("Thank you!");
              setModalText("We will get back to you shortly.");
              setTimeout(modalRef.current.hide, 5000);

              //send gtm datalayer event if possible
              try {
                window.dataLayer.push({"event": "contactFormSubmitted"});
              } catch (err){}
            } else {
              setCta("Error");
              modalRef.current.show();
              setModalHeader("Error");
              setModalText("Please try again.");
              setTimeout(modalRef.current.hide, 5000);
            }
          });
      } else {
        setAllowSubmit(true);
      }
    }
  }

  function invokeCaptcha(){
    if (!captchaToken){
      captchaRef.current.execute();
    } else {
      submit();
    }
  }

  //track abandoned forms
  useEffect(() => {
    const sendEvent = () => {
      if (window.lastDirtied){
        sendDatalayerEvent("formAbandoned", "Form Abandonment", window.lastDirtied);
        window.lastDirtied = null;
      }
    };

    window.addEventListener("beforeunload", sendEvent);
    window.addEventListener("visibilitychange", (e) => {
      if (document.visibilityState === "hidden"){
        sendEvent();
      }
    });
    document.addEventListener("visibilitychange", (e) => {
      if (document.visibilityState === "hidden"){
        sendEvent();
      }
    });
    window.addEventListener("pagehide", sendEvent);
    window.addEventListener("popstate", sendEvent);

    router.events.on("routeChangeStart", sendEvent);
    return (a, b, c) => {
      window.removeEventListener("beforeunload", sendEvent);
      window.removeEventListener("visibilitychange", sendEvent);
      document.removeEventListener("visibilitychange", sendEvent);
      window.removeEventListener("pagehide", sendEvent);
      window.removeEventListener("popstate", sendEvent);
      router.events.off("routeChangeStart", sendEvent);
      window.lastDirtied = false;
    };
  }, [router.events]);


  let thisClassName = `section ${css.contact}`;
  thisClassName += compact ? ` ${css.compact}` : "";

  return (<>
    <Modal ref={modalRef}>
      <div className="h2" id="contactModal">{modalHeader}</div>
      <div>{modalText}</div>
    </Modal>

    <section className={thisClassName} id="contact" style={{"--color": `var(--${color})`}}>
      <div className="sectionInner">
        <h2>
          <RevealedText
            texts={[
              "Contact and Work",
              "With Deviate Labs",
            ]}
            color="#ffffff"
          />
        </h2>

        <div className={css.form}>
          {color === "yellow" && <picture className={css.pulsingImage}>
            <source srcSet="/images/floatingImages/phone/phone-180w.webp 180w,/images/floatingImages/phone/phone-384w.webp 384w,/images/floatingImages/phone/phone-640w.webp 640w" type="image/webp"/>
            <source srcSet="/images/floatingImages/phone/phone-180w.png 180w,/images/floatingImages/phone/phone-384w.png 384w,/images/floatingImages/phone/phone-640w.png 640w" type="image/png"/>
            <img loading="eager" src="/images/floatingImages/phone/phone-640w.png" alt=""/>
          </picture>}
          <FloatingTextInput
            label="Name"
            ref={form.name}
            validators={[
              { test: (val) => /^$/.test(val), class: "invalid", message: "Required" },
              { test: (val) => /^..*$/.test(val), class: "valid", message: "" },
            ]}
            onFinish={() => (window.lastDirtied = "name")}
          />
          <FloatingTextInput
            label="Email"
            ref={form.email}
            validators={[
              { test: (val) => /^$/.test(val), class: "invalid", message: "Required" },
              { test: (val) => /^..*@.*$/.test(val), class: "valid", message: "" },
              { test: (val) => /.*/.test(val), class: "invalid", message: "Must have an @" },
            ]}
            onFinish={() => (window.lastDirtied = "email")}
          />
          <FloatingTextInput
            label="Topic"
            className={css.topic}
            ref={form.topic}
            validators={[
              { test: (val) => /^$/.test(val), class: "invalid", message: "Required" },
              { test: (val) => /^..*$/.test(val), class: "valid", message: "" },
            ]}
            onFinish={() => (window.lastDirtied = "topic")}
          />
          <FloatingTextInput
            multiline
            label="Message"
            className={css.message}
            ref={form.message}
            validators={[
              { test: (val) => /^$/.test(val), class: "invalid", message: "Required" },
              { test: (val) => /^..*$/.test(val), class: "valid", message: "" },
            ]}
            onFinish={() => (window.lastDirtied = "message")}
          />
          <Checkbox
            className={css.tos}
            name="tos"
            ref={form.tos}
            label="I agree to keep all discussions with Deviate Labs private. By checking this box I understand that I am NOT allowed to disclose any growth hacks or clients communicated to me by Deviate Labs."
            validators={[
              { test: (val) => !val, class: "invalid", message: "Required" },
              { test: (val) => val, class: "valid", message: "" },
            ]}
            onFinish={() => (window.lastDirtied = "tos")}
          />
          <div className={css.captcha}>
            <HCaptcha
              sitekey={process.env.NEXT_PUBLIC_HCAPTCHA_SITEKEY}
              onVerify={setCaptchaToken}
              ref={captchaRef}
            />
          </div>
          <div className={css.submit}>
            <Button label={cta} onClick={invokeCaptcha} ghost light color="yellow" className={css.button}/>
          </div>

        </div>

        {!compact && <div className={`slideIn ${css.info}`}>
          <div className={css.entry}>
            <svg width="25" height="30" xmlns="http://www.w3.org/2000/svg" className="d-block pull-left svg-stroke">
              <g stroke="#ffffff" strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" fill="transparent" fillRule="evenodd">
                <path d="M12.5 28S23 18.144 23 12.4 18.299 2 12.5 2 2 6.656 2 12.4C2 18.144 12.5 28 12.5 28zM16 12.5a3.5 3.5 0 10-3.5 3.5"/>
              </g>
            </svg>
            <div className={css.entryContent}>
              <div className={css.entryKey}>8605 Santa Monica Blvd 93568</div>
              <div className={css.entryValue}>Los Angeles, California 90069</div>
            </div>
          </div>
          <div className={css.entry}>
            <svg width="25" height="30" xmlns="http://www.w3.org/2000/svg" className="d-block pull-left svg-stroke">
              <g stroke="#ffffff" strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" fill="transparent" fillRule="evenodd">
                <path d="M12.5 28S23 18.144 23 12.4 18.299 2 12.5 2 2 6.656 2 12.4C2 18.144 12.5 28 12.5 28zM16 12.5a3.5 3.5 0 10-3.5 3.5"/>
              </g>
            </svg>
            <div className={css.entryContent}>
              <div className={css.entryKey}>113 Cherry Street</div>
              <div className={css.entryValue}>Seattle, Washington 98104</div>
            </div>
          </div>
          <div className={css.entry}>
            <svg width="30" height="31" xmlns="http://www.w3.org/2000/svg" className="d-block pull-left svg-stroke">
              <path stroke="#ffffff" fill="#ffffff" d="M11.857 19.243l-3.643-3.615a.867.867 0 01-.273-.627c0-.236.098-.462.273-.628l2.267-2.16a1.677 1.677 0 000-2.448L7.892 7.297c-.686-.654-1.882-.655-2.568 0a.97.97 0 01-.135.106c-.107.071-2.706 1.844-2.28 5.503.058.488-.31.926-.821.98-.507.056-.972-.296-1.028-.784a7.59 7.59 0 013.004-7.115c1.394-1.275 3.772-1.253 5.144.053l2.588 2.469a3.396 3.396 0 010 4.958l-1.606 1.534 2.866 2.91a.906.906 0 01.165.136l3.095 3.233 1.544-1.612a3.43 3.43 0 012.497-1.08c.944 0 1.83.383 2.498 1.08l2.486 2.598a3.746 3.746 0 011.034 2.607 3.75 3.75 0 01-.98 2.551c-.326.46-2.081 2.712-5.374 3.037a7.96 7.96 0 01-.806.039c-3.486 0-7.345-2.17-11.487-6.463a.47.47 0 01-.084-.07c-2.14-2.264-2.76-2.785-3.983-4.968-.245-.437-.052-1.008.375-1.241.43-.233.903-.02 1.173.397 1.292 2.002 2.1 2.772 4.081 4.867 3.9 3.949 7.537 5.873 10.563 5.58 2.763-.274 4.103-2.285 4.116-2.306a.856.856 0 01.107-.135 1.85 1.85 0 00.51-1.288c0-.485-.18-.943-.51-1.287l-2.485-2.597a1.696 1.696 0 00-2.465 0l-2.178 2.272a.866.866 0 01-1.264 0l-3.727-3.894a.934.934 0 01-.1-.124zm11.843-4.83a.878.878 0 01-.877-.879c-.003-3.214-2.6-5.818-5.803-5.822a.878.878 0 01-.877-.88c0-.485.393-.88.877-.88 4.167 0 7.557 3.403 7.557 7.582a.881.881 0 01-.877.88zm4.613-.908a.878.878 0 01-.877-.88c0-5.26-4.268-9.54-9.51-9.54a.878.878 0 01-.877-.88c0-.486.393-.88.877-.88 6.211 0 11.264 5.07 11.264 11.3 0 .486-.392.88-.877.88z" fillRule="nonzero" strokeWidth=".9"/>
            </svg>
            <div className={css.entryContent}>
              <div className={css.entryKey}>Call us at</div>
              <div className={css.entryValue}>
                <a className="custom" href="tel:+18887338609">+1 (888) 733-8609</a>
              </div>
            </div>
          </div>
          <div className={css.entry}>
            <svg width="27" height="23" xmlns="http://www.w3.org/2000/svg" className="d-block pull-left svg-fill">
              <path fill="#ffffff" d="M1.5 3.5a1.5 1.5 0 010-3h24a1.5 1.5 0 010 3h-24zm22.5 16V2a1.5 1.5 0 013 0v19a1.5 1.5 0 01-1.5 1.5h-24A1.5 1.5 0 010 21V8.685a1.5 1.5 0 013 0V19.5h21z"/>
              <path fill="#ffffff" d="M18.525 7.86a1.5 1.5 0 011.95 2.28l-5.845 5a1.5 1.5 0 01-1.92.024l-6.156-5a1.5 1.5 0 011.892-2.328l5.184 4.211 4.895-4.187z"/>
            </svg>

            <div className={css.entryContent}>
              <div className={css.entryKey}>Drop us a line at</div>
              <div className={css.entryValue}>
                <a className="custom" href="mailto:hello@deviatelabs.com">hello@deviatelabs.com</a>
              </div>
            </div>
          </div>
        </div>}
      </div>
    </section>
  </>);
}



